import type {
  LegalEntity,
  LegalEntityFormData,
  LegalEntityStatus
} from "../types/legalEntity";
import { mockLegalEntities } from "../data/mockLegalEntities";
import { formatDisplayDate } from "../utils/displayDate";
import { formatPhoneForApiPayload } from "../utils/validation/legalEntityValidation";
import { apiRequest } from "./apiClient";
let inMemoryLegalEntities: LegalEntity[] = [...mockLegalEntities];

type BackendPaginatedResponse<T> = {
  readonly success?: boolean;
  readonly data: T[];
  readonly meta?: {
    readonly total?: number;
    readonly limit?: number;
    readonly offset?: number;
  };
  readonly message?: string;
};

type BackendSuccessResponse<T> = {
  readonly success?: boolean;
  readonly data: T;
  readonly message?: string;
};

type BackendLegalEntityItem = {
  readonly id: string;
  readonly entityName?: string | null;
  /** Contact name (not company). */
  readonly name?: string | null;
  readonly title?: string | null;
  readonly company?: string | null;
  readonly streetAddress?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly county?: string | null;
  readonly zip?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  /** Denormalized full line from API (street, city, state, zip). */
  readonly address?: string | null;
  readonly status?: LegalEntityStatus | null;
  readonly createdOn?: string | null;
  readonly modifiedOn?: string | null;
  readonly createdBy?: string | null;
  readonly modifiedBy?: string | null;
};

export type LegalEntitySortBy =
  | "created_at"
  | "created_by"
  | "updated_by"
  | "updated_at"
  | "entity_name"
  | "name"
  | "title";

export type LegalEntityListParams = {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string;
  readonly status?: LegalEntityStatus[];
  readonly updatedBy?: string[];
  readonly sort_by?: LegalEntitySortBy;
  readonly sort_dir?: "asc" | "desc";
};

export type LegalEntityListResult = {
  readonly items: LegalEntity[];
  readonly total: number;
};

const toLegalEntity = (item: BackendLegalEntityItem): LegalEntity => {
  const entityName = (item.entityName ?? item.company ?? "").trim();
  const streetAddress = (item.streetAddress ?? "").trim();
  const city = (item.city ?? "").trim();
  const state = (item.state ?? "").trim();
  const county = (item.county ?? "").trim();
  const zip = (item.zip ?? "").trim();
  const mergedAddress = [streetAddress, city, state, zip].filter(Boolean).join(", ");

  return {
    id: item.id,
    entityName: entityName || "Unknown",
    name: (item.name ?? "").trim(),
    title: (item.title ?? "").trim(),
    company: (item.company ?? entityName).trim(),
    streetAddress,
    city,
    state,
    ...(county ? { county } : {}),
    zip,
    phone: (item.phone ?? "").trim(),
    email: (item.email ?? "").trim(),
    address: (item.address ?? mergedAddress).trim(),
    createdBy: item.createdBy ?? "Unknown",
    createdOn: item.createdOn
      ? formatDisplayDate(item.createdOn)
      : formatDisplayDate(new Date()),
    modifiedBy: item.modifiedBy ?? "Unknown",
    modifiedOn: item.modifiedOn ? formatDisplayDate(item.modifiedOn) : undefined,
    status: item.status ?? "Inactive"
  };
};

export const listLegalEntities = async (
  params: LegalEntityListParams
): Promise<LegalEntityListResult> => {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit));
  q.set("offset", String(params.offset));
  if (params.q?.trim()) {
    q.set("q", params.q.trim());
  }
  for (const s of params.status ?? []) {
    q.append("status", s);
  }
  for (const u of params.updatedBy ?? []) {
    q.append("updated_by", u);
  }
  if (params.sort_by) {
    q.set("sort_by", params.sort_by);
  }
  if (params.sort_dir) {
    q.set("sort_dir", params.sort_dir);
  }

  try {
    const response = await apiRequest(`/legal-entities?${q.toString()}`);

    const json = (await response.json()) as BackendPaginatedResponse<BackendLegalEntityItem>;
    const items = Array.isArray(json.data) ? json.data.map(toLegalEntity) : [];
    const total = typeof json.meta?.total === "number" ? json.meta.total : items.length;
    return { items, total };
  } catch {
    // Mock fallback for local development
    const query = (params.q ?? "").trim().toLowerCase();
    const filtered = inMemoryLegalEntities.filter((entity) => {
      const matchesQuery =
        !query ||
        entity.name.toLowerCase().includes(query) ||
        entity.address.toLowerCase().includes(query) ||
        entity.entityName.toLowerCase().includes(query) ||
        entity.title.toLowerCase().includes(query);
      const matchesStatus =
        !params.status || params.status.length === 0 || params.status.includes(entity.status);
      return matchesQuery && matchesStatus;
    });
    return { items: filtered, total: filtered.length };
  }
};

export const createLegalEntity = async (
  formData: LegalEntityFormData
): Promise<LegalEntity> => {
  try {
    const response = await apiRequest("/legal-entities", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: formData.phone.trim() ? formatPhoneForApiPayload(formData.phone) : "",
        county: formData.county?.trim() ? formData.county.trim() : undefined
      })
    });

    const json = (await response.json()) as BackendSuccessResponse<BackendLegalEntityItem>;
    return toLegalEntity(json.data);
  } catch {
    const nowIso = new Date().toISOString();
    const created: LegalEntity = {
      id: `mock-${Date.now()}`,
      entityName: formData.company.trim(),
      name: formData.name.trim(),
      title: formData.title.trim(),
      company: formData.company.trim(),
      streetAddress: formData.streetAddress.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      ...(formData.county?.trim() ? { county: formData.county.trim() } : {}),
      zip: formData.zip.trim(),
      phone: formData.phone.trim() ? formatPhoneForApiPayload(formData.phone) : "",
      email: formData.email.trim(),
      address: `${formData.streetAddress.trim()}, ${formData.city.trim()}, ${formData.state.trim()} ${formData.zip.trim()}`,
      createdBy: "Current User (current.user@example.com)",
      createdOn: formatDisplayDate(nowIso),
      modifiedBy: "Current User (current.user@example.com)",
      modifiedOn: formatDisplayDate(nowIso),
      status: "Active"
    };
    inMemoryLegalEntities = [created, ...inMemoryLegalEntities];
    return created;
  }
};

export const updateLegalEntityStatus = async (
  id: string,
  status: LegalEntityStatus
): Promise<LegalEntityStatus> => {
  try {
    const response = await apiRequest(`/legal-entities/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status })
    });

    const json = (await response.json()) as BackendSuccessResponse<BackendLegalEntityItem>;
    return json.data.status ?? status;
  } catch {
    inMemoryLegalEntities = inMemoryLegalEntities.map((entity) =>
      entity.id === id ? { ...entity, status } : entity
    );
    return status;
  }
};
